import Dexie, { type Table } from 'dexie';

export interface Note {
  id?: string;
  verseId: string;
  verseReference: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  // Additional fields for Read in Context
  book?: string;
  chapter?: number;
  verse?: number;
  version?: string;
}

class NotesDatabase extends Dexie {
  notes!: Table<Note>;

  constructor() {
    super('divine-intelligence-notes');
    
    this.version(1).stores({
      notes: '++id, verseId, verseReference, createdAt, updatedAt'
    });
    
    this.version(2)
      .stores({
        notes: '++id, verseId, verseReference, book, chapter, verse, version, createdAt, updatedAt'
      })
      .upgrade(tx => {
        return tx.table('notes').toCollection().modify(note => {
          // Extract book, chapter, and verse from verseReference
          const [book, chapterVerse] = note.verseReference.split(' ');
          const [chapter, verse] = chapterVerse.split(':').map(Number);
          
          note.book = book;
          note.chapter = chapter;
          note.verse = verse;
          note.version = 'kjv'; // Default to KJV for existing notes
        });
      });
  }
}

export const db = new NotesDatabase();

// Generate a UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Notes API
export const notesApi = {
  async addNote(
    verseId: string, 
    verseReference: string, 
    content: string,
    book: string,
    chapter: number,
    verse: number,
    version: string
  ): Promise<string> {
    try {
      console.log('Adding note:', { verseId, verseReference, content });
      const newNote: Note = {
        id: generateUUID(),
        verseId,
        verseReference,
        content,
        book,
        chapter,
        verse,
        version,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await db.notes.add(newNote);
      console.log('Note added successfully:', newNote.id);
      return newNote.id!;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  },

  async updateNote(noteId: string, content: string): Promise<void> {
    try {
      console.log('Updating note:', { noteId, content });
      await db.notes.update(noteId, {
        content,
        updatedAt: new Date().toISOString()
      });
      console.log('Note updated successfully');
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  async deleteNote(noteId: string): Promise<void> {
    try {
      console.log('Deleting note:', noteId);
      await db.notes.delete(noteId);
      console.log('Note deleted successfully');
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  async getNotesByVerse(verseId: string): Promise<Note[]> {
    try {
      console.log('Getting notes for verse:', verseId);
      const notes = await db.notes.where('verseId').equals(verseId).toArray();
      console.log('Found notes:', notes.length);
      return notes;
    } catch (error) {
      console.error('Error getting notes:', error);
      throw error;
    }
  },

  async getAllNotes(): Promise<Note[]> {
    try {
      console.log('Getting all notes');
      const notes = await db.notes.orderBy('createdAt').reverse().toArray();
      console.log('Found total notes:', notes.length);
      return notes;
    } catch (error) {
      console.error('Error getting all notes:', error);
      // Initialize empty array if there's an error
      return [];
    }
  },

  async hasNotes(verseId: string): Promise<boolean> {
    try {
      console.log('Checking for notes on verse:', verseId);
      const count = await db.notes.where('verseId').equals(verseId).count();
      console.log('Notes count:', count);
      return count > 0;
    } catch (error) {
      console.error('Error checking for notes:', error);
      throw error;
    }
  },

  async clearDatabase(): Promise<void> {
    try {
      console.log('Clearing notes database...');
      await db.notes.clear();
      console.log('Notes database cleared successfully');
    } catch (error) {
      console.error('Error clearing notes database:', error);
    }
  },
};
